<script setup lang="ts">
import { $t } from '@teachino/shared-i18n';
import { formatSecondsAsMinutesSeconds } from '@teachino/shared-util';
import { computed, onUnmounted, ref } from 'vue';
import DropdownMenu from '../DropdownMenu/DropdownMenu.vue';
import type { DropdownMenuItem } from '../DropdownMenu/dropdown-menu';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import IconButton from '../IconButton/IconButton.vue';
import {
  type AudioInputDevice,
  useSpeechToTextRecording,
} from './useSpeechToTextRecording';

const MIN_RECORDING_INTENSITY = 0.12;
const COUNTDOWN_INTERVAL_MS = 250;
const STOP_BUTTON_MAX_SCALE = 1.06;
const STOP_BUTTON_SCALE_RANGE = 0.14;
const BUTTON_STYLE_PRECISION = 3;
const MS_PER_SECOND = 1000;

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    maxDurationSeconds?: number;
    transcribe: (audioBlob: Blob) => Promise<string>;
    microphoneLabel?: string;
    microphoneDefaultLabel?: string;
    microphoneDeniedErrorLabel?: string;
    transcriptionErrorLabel?: string;
  }>(),
  {
    disabled: false,
    maxDurationSeconds: undefined,
    microphoneLabel: $t('speech_to_text.microphone'),
    microphoneDefaultLabel: $t('speech_to_text.default_microphone'),
    microphoneDeniedErrorLabel: $t('speech_to_text.microphone_denied'),
    transcriptionErrorLabel: $t('speech_to_text.transcription_error'),
  },
);

const emit = defineEmits<{
  error: [error: unknown];
  recorded: [audioBlob: Blob];
  transcribed: [text: string];
}>();

const audioRecording = useSpeechToTextRecording();
const isTranscribing = ref(false);
const selectedAudioInputDeviceId = audioRecording.selectedInputDeviceId;
const transcriptionError = ref('');
const remainingDurationMs = ref(0);

let activeSessionToken = 0;
let recordingTask: Promise<void> | null = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const isBusy = computed(() => props.disabled || isTranscribing.value);
const effectiveMaxDurationSeconds = computed(
  () => props.maxDurationSeconds ?? audioRecording.defaultMaxDurationSeconds,
);
const recordingIntensity = computed(() =>
  Math.min(
    1,
    Math.max(MIN_RECORDING_INTENSITY, audioRecording.inputVolumeLevel.value),
  ),
);
const remainingDurationLabel = computed(() =>
  formatDurationMs(
    remainingDurationMs.value ||
      effectiveMaxDurationSeconds.value * MS_PER_SECOND,
  ),
);
const deviceMenuItems = computed<DropdownMenuItem[]>(() =>
  audioRecording.inputDevices.value.map((device, index) => ({
    icon:
      device.deviceId === selectedAudioInputDeviceId.value
        ? 'checkmark'
        : undefined,
    label: getAudioInputLabel(device, index),
    onClick: () => {
      void selectInputDevice(device.deviceId);
    },
  })),
);

const stopButtonStyle = computed(() => {
  const buttonScale = Math.min(
    STOP_BUTTON_MAX_SCALE,
    1 + recordingIntensity.value * STOP_BUTTON_SCALE_RANGE,
  );

  return {
    transform: `scale(${buttonScale.toFixed(BUTTON_STYLE_PRECISION)})`,
  };
});

function getAudioInputLabel(device: AudioInputDevice, index: number) {
  if (device.label) {
    return device.label;
  }

  if (device.deviceId === 'default') {
    return props.microphoneDefaultLabel;
  }

  return `${props.microphoneLabel} ${String(index + 1)}`;
}

function formatDurationMs(durationMs: number) {
  const safeDurationMs = Math.max(0, durationMs);
  return formatSecondsAsMinutesSeconds(
    Math.ceil(safeDurationMs / MS_PER_SECOND),
  );
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function startCountdown() {
  stopCountdown();

  const maxDurationMs = effectiveMaxDurationSeconds.value * MS_PER_SECOND;
  const deadline = Date.now() + maxDurationMs;
  remainingDurationMs.value = maxDurationMs;

  countdownTimer = setInterval(() => {
    remainingDurationMs.value = Math.max(0, deadline - Date.now());
    if (remainingDurationMs.value === 0) {
      stopCountdown();
    }
  }, COUNTDOWN_INTERVAL_MS);
}

async function runRecordingSession() {
  if (
    props.disabled ||
    isTranscribing.value ||
    audioRecording.isRecording.value
  ) {
    return;
  }

  transcriptionError.value = '';
  const sessionToken = activeSessionToken + 1;
  activeSessionToken = sessionToken;

  recordingTask = (async () => {
    try {
      const recordingPromise = audioRecording.start(
        effectiveMaxDurationSeconds.value,
      );
      startCountdown();
      const audioBlob = await recordingPromise;
      if (sessionToken !== activeSessionToken) {
        return;
      }

      emit('recorded', audioBlob);
      isTranscribing.value = true;

      const text = await props.transcribe(audioBlob);
      if (sessionToken === activeSessionToken && text) {
        emit('transcribed', text);
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        return;
      }

      console.error(error);
      emit('error', error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        transcriptionError.value = props.microphoneDeniedErrorLabel;
      } else {
        transcriptionError.value = props.transcriptionErrorLabel;
      }
    } finally {
      stopCountdown();
      if (sessionToken === activeSessionToken) {
        isTranscribing.value = false;
      }
      recordingTask = null;
    }
  })();

  await recordingTask;
}

function startRecording() {
  void runRecordingSession();
}

function stopRecording() {
  audioRecording.stop();
}
async function selectInputDevice(deviceId: string) {
  const deviceChanged = selectedAudioInputDeviceId.value !== deviceId;
  selectedAudioInputDeviceId.value = deviceId;

  if (!deviceChanged || !audioRecording.isRecording.value) {
    return;
  }

  activeSessionToken += 1;
  audioRecording.abort();
  await recordingTask?.catch(() => undefined);
  startRecording();
}

onUnmounted(() => {
  stopCountdown();
  audioRecording.abort();
});
</script>
<template>
  <div
    v-if="audioRecording.isSupported.value"
    class="relative inline-flex flex-col items-end"
  >
    <IconButton
      v-if="!audioRecording.isRecording.value"
      type="button"
      icon="mic"
      size="sm"
      variant="ghost-neutral"
      :tooltip="
        $t(
          isTranscribing
            ? 'speech_to_text.transcribing'
            : 'speech_to_text.start_recording',
        )
      "
      :disabled="isBusy"
      :loading="isTranscribing"
      :aria-label="
        $t(
          isTranscribing
            ? 'speech_to_text.transcribing'
            : 'speech_to_text.start_recording',
        )
      "
      @click="startRecording"
    />

    <div
      v-else
      class="relative inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white"
    >
      <DropdownMenu
        v-if="audioRecording.inputDevices.value.length > 0"
        :items="deviceMenuItems"
        placement="bottom-start"
      >
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 transition-[background-color,color] duration-150 ease-out hover:bg-neutral-100 hover:text-neutral-700"
        >
          <HugeIcon icon="chevron_down" :size="16" />
        </button>
      </DropdownMenu>

      <span
        class="min-w-11 text-center text-sm font-medium text-slate-500 tabular-nums"
      >
        {{ remainingDurationLabel }}
      </span>

      <IconButton
        type="button"
        icon="stop"
        size="sm"
        variant="ghost-neutral"
        :tooltip="$t('speech_to_text.stop_recording')"
        :style="stopButtonStyle"
        :aria-label="$t('speech_to_text.stop_recording')"
        @click="stopRecording"
      />
    </div>

    <div
      v-if="transcriptionError"
      class="text-danger-500 absolute top-full right-0 z-10 mt-2 w-64 text-sm"
    >
      {{ transcriptionError }}
    </div>
  </div>
</template>
